import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  limit,
  Timestamp
} from 'firebase/firestore';
import { db } from '@/services/firebase';
import { ErrorDocument } from '@/types';

interface UseErrorsOptions {
  resolved?: boolean;
  service?: string;
  severity?: string;
  limitCount?: number;
  timeWindowHours?: number;
}

export function useErrors(options: UseErrorsOptions = {}) {
  const [errors, setErrors] = useState<ErrorDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const {
    resolved = false,
    service,
    severity,
    limitCount = 100,
    timeWindowHours = 24,
  } = options;

  useEffect(() => {
    setLoading(true);
    setError(null);

    try {
      const errorsRef = collection(db, 'errors');

      // Build query constraints.
      //
      // Keyed on `lastSeen`, NOT `timestamp`. `timestamp` is when an error was
      // FIRST recorded and is never updated again; deduplication only bumps
      // `count` and `lastSeen`. Filtering on `timestamp` therefore hid any
      // recurring error whose first occurrence fell outside the window — on
      // 2026-08-26 a chat error that had just fired (and paged Slack with 112
      // occurrences) was invisible here because it was first seen 2025-12-08.
      // "Is it failing now?" is a question about lastSeen.
      const constraints: any[] = [
        where('resolved', '==', resolved),
        orderBy('lastSeen', 'desc'),
        limit(limitCount),
      ];

      // Add time window filter
      if (timeWindowHours > 0) {
        const cutoff = new Date(Date.now() - timeWindowHours * 60 * 60 * 1000);
        constraints.splice(1, 0, where('lastSeen', '>=', Timestamp.fromDate(cutoff)));
      }

      const q = query(errorsRef, ...constraints);

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          let docs = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          })) as ErrorDocument[];

          // Client-side filtering for service and severity
          // (Firestore doesn't support multiple inequality filters)
          if (service) {
            docs = docs.filter(d => d.service === service);
          }
          if (severity) {
            docs = docs.filter(d => d.severity === severity);
          }

          setErrors(docs);
          setLoading(false);
        },
        (err) => {
          console.error('Error fetching errors:', err);
          setError(err);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } catch (err) {
      setError(err as Error);
      setLoading(false);
    }
  }, [resolved, service, severity, limitCount, timeWindowHours]);

  return { errors, loading, error };
}

export function useRecentErrors(limitCount: number = 50) {
  return useErrors({
    resolved: false,
    limitCount,
    timeWindowHours: 24
  });
}

export function useAllErrors(timeWindowHours: number = 168) { // 7 days
  return useErrors({
    timeWindowHours,
    limitCount: 500,
  });
}
